import { Router } from "@angular/router";
import { environment } from "environment";
import { appRoute } from "src/app/app-route.constants";
import { reactAppSegment } from "./myzpax-routes.util";

export const legacyHomeUrl = `/${appRoute.home}`;
export const reactHomeUrl = `/${reactAppSegment}/${appRoute.home}`;

const bounceCookie = "rc-home-bounces";
const bounceWindowSeconds = 30;
const bounceLimit = 3;

const sessionUser = (): { npi?: unknown } | null => {
  const raw = sessionStorage.getItem("user");
  if (!raw) return null;
  try {
    return JSON.parse(raw) as { npi?: unknown };
  } catch {
    return null;
  }
};

/**
 * React renders a page only when it can see *both* shared session keys, and it
 * bounces NPI sessions straight back out to Angular. Angular's own guards are
 * satisfied by `user` alone, so unless the handoff applies React's rule the two
 * apps can disagree about who is logged in and ping-pong the browser between
 * `/` and `/rc/home` as fast as it can reload.
 */
export const reactWillAcceptSession = (): boolean => {
  if (sessionStorage.getItem("access_token") === null) return false;
  const user = sessionUser();
  return user !== null && !user.npi;
};

const readBounces = (): number => {
  const match = document.cookie.match(
    new RegExp(`(?:^|; )${bounceCookie}=(\\d+)`)
  );
  return match ? Number(match[1]) : 0;
};

/**
 * Records one post-login handoff into React. Deliberately called *only* from
 * the `/validate` restore: that is the single step every lap of the loop passes
 * through, so counting it cannot be confused with ordinary navigation. Counting
 * every handoff would strand a healthy user on the legacy page for clicking
 * Home a few times in quick succession.
 *
 * React wipes `sessionStorage` and `localStorage` when it rejects a session, so
 * a cookie is the only counter that survives a lap.
 */
export const recordReactHandoff = (): void => {
  document.cookie = `${bounceCookie}=${readBounces() + 1}; path=/; max-age=${bounceWindowSeconds}; SameSite=Lax`;
};

/**
 * Circuit breaker for the Angular → React → login → Angular cycle. If React
 * keeps rejecting what Angular considers a good session — an API returning 401,
 * a session key the two apps read differently — every lap re-authenticates
 * successfully and lands back on React, so the loop never terminates on its
 * own. Three full logins inside the window is not something a real user does,
 * so treat it as a loop, keep them in Angular, and let a real page render.
 * Self-heals: the cookie expires once the bouncing stops.
 */
export const reactHandoffIsLooping = (): boolean => readBounces() >= bounceLimit;

/**
 * True when it is safe to send the user into the React app. Everything that
 * navigates to Home must go through this rather than checking the flag alone.
 */
export const canHandOffToReact = (): boolean =>
  environment.enableNewReactApp &&
  reactWillAcceptSession() &&
  !reactHandoffIsLooping();

/**
 * Home lives in the React app once `enableNewReactApp` is on. The Angular
 * `/home` route stays registered so the flag can be flipped back, so the React
 * "Use legacy view" escape hatch keeps working, and so there is somewhere safe
 * to land when React will not take the session.
 */
export const homeUrl = (): string =>
  canHandOffToReact() ? reactHomeUrl : legacyHomeUrl;

/**
 * React lives outside the Angular router, so reaching it always needs a
 * full-page load rather than a `Router.navigate`. Falls back to the Angular
 * home page whenever React would not accept the session, which keeps
 * unauthenticated and NPI users on the side of the app whose guards can drive a
 * real SSO login.
 */
export const navigateHome = (router: Router, replaceUrl = false): void => {
  if (canHandOffToReact()) {
    if (replaceUrl) {
      window.location.replace(reactHomeUrl);
    } else {
      window.location.href = reactHomeUrl;
    }
    return;
  }
  router.navigate([appRoute.home], { replaceUrl });
};
